<template>
    <div id="template-list">
        <div id="worksends-page-header" class="tw-mb-3">
            <div class="breadcrumbs" id="breadcrumbs">
                <router-link class="breadcrumb-link" to="/surveys">Bookblock HUB Surveys</router-link> &gt;
                <router-link class="breadcrumb-link" to="/surveys/templates">Templates</router-link>
            </div>
            <h3>Bookblock HUB Survey Templates</h3>
            <div v-if="isAdmin" class="ws-button ws-button-admin" @click="onShowCreate">Create A New Template</div>
            <div class="category-filter tw-float-right">
                <div class="ws-filter ws-filter-select">
                    <WSSelect v-model="filterByCategory" style="min-width: 150px;" :options="[
                        {value: '', text: 'All Categories'},
                        ...surveyTypes
                    ]" />
                </div>
            </div>
        </div>
        <div v-if="loaded && showJumbotron" class="jumbotron tw-p-5 tw-rounded tw-flex tw-justify-between tw-flex-col">
            <div class="tw-flex tw-justify-between tw-items-start">
                <h2 class="tw-text-white tw-text-lg">Welcome To Industry-Optimised Customisable Survey Templates.</h2>
                <a class="hide tw-text-white tw-text-xs" @click="hideJumbotron">Hide</a>
            </div>
            <div class="desc text-white">
                <p>
                    Gain customer insight by asking just the right questions. Choose to customise one of our industry-optimised
                    survey templates tailored for any application.
                </p>
            </div>
            <div class="jumbotron-buttons">
                <!-- <router-link class="ws-button ws-button-white" to="/surveys/templates">
                    Browse Template Gallery
                </router-link>
                <a href="#" class="ws-button ws-button-white" @click.stop="onShowCreate">Start From Scratch</a> -->
            </div>
        </div>
        <draggable class="grid templates tw-mt-4" v-model="templateIds" handle=".drag-handle">
            <div v-for="template in templates" :key="template.id" class="item template tw-bg-white tw-rounded" @click="showPreview(template.id)">
                <div v-if="isAdmin" class="drag-handle tw-cursor-grab p-2">Drag to reorder</div>
                <div class="tw-rounded tw-flex tw-flex-col" >
                    <div class="page-preview">
                        <page-view v-if="template.pages && template.pages[0]"
                                   :page="startingPage(template.pages)"
                                   :styles="template.theme ? template.theme.styles : {}"
                                   :editable="false"
                                   :readonly="true"
                                   view="preview"
                                   storePrefix="surveyTemplates"
                        />
                    </div>
                    
                    <h3 class="tw-font-normal">{{ template.name }}</h3>
                    <div class="category">{{ templateTypeLabel(template.category) }}</div>
                    <div v-if="isAdmin" class="status" :class="'status-'+template.status">{{ template.status }}</div>
                    
                    <div class="description">{{ template.description }}</div>
                </div>
                <div class="buttons tw-flex tw-justify-between tw-mt-3">
                    <div class="ws-button ws-button-main small">Preview</div>
                    <div class="ws-button ws-button-secondary small" @click.stop="activateTemplate(template.id)">Activate Template</div>
                </div>
            </div>
        </draggable>

        <modal v-if="showCreateModal" @close="showCreateModal = false">
            <template-settings-form @save="onCreateTemplate" @cancel="onCloseCreate"/>
        </modal>

        <modal v-if="showPreviewModal" @close="showPreviewModal = false" class="survey-preview-modal-window">
            <template-details :template-preview="previewTemplateId" @activate="activateTemplate" />
        </modal>
    </div>
</template>

<script>
import Modal from '@/components/modals/Modal';
import TemplateSettingsForm from "@/pages/surveys/TemplateSettingsForm";
import draggable from 'vuedraggable'
import PageView from "@/pages/surveys/PageView.vue";
import WSSelect from "@/components/elements/WSSelect.vue";
import TemplateDetails from "@/pages/surveys/TemplateDetails.vue";

export default {
    name: 'TemplateIndex',
    components: {TemplateDetails, WSSelect, PageView, TemplateSettingsForm, Modal, draggable},
    data() {
        return {
            jumbotron: true,
            filterByCategory: '',
            showCreateModal: false,
            showPreviewModal: false,
            previewTemplateId: undefined,
            loaded: false
        }
    },
    watch: {
        filterByCategory: 'onLoadTemplates'
    },
    computed: {
        templates() {
            return this.$store.getters['surveyTemplates/orderedList']
        },
        surveyTypes() {
            return this.$store.state.surveyTypes;
        },
        templateIds: {
            get() {
                return this.$store.state.surveyTemplates.ids;
            },
            set(ids) {
                this.$store.dispatch('surveyTemplates/reorder', ids.filter(id => id))
            },
        },
        showJumbotron() {
            return this.$cookies.get('surveys_templates_jumbotron_hidden') ? false : this.jumbotron;
        },
        isAdmin() {
            return this.$store.state.currentUser.is_bb_admin;
        },
        templateTypes() {
            return this.$store.state.surveyTypes;
        }
    },
    created() {
        window.vm = this;
        this.onLoadTemplates()
    },
    methods: {
        startingPage(pages) {
            if (pages.filter(p => p.order === 1).length) return pages.filter(p => p.order === 1)[0];
            else return pages[0];
        },
        onShowCreate() {
            this.showCreateModal = true;
        },
        onCloseCreate() {
            this.showCreateModal = false;
        },
        goToDetail(id) {
            this.$router.push({ name: 'surveys-template-details', params: { template_id: id } });
        },
        hideJumbotron() {
            this.jumbotron = false;
            this.$cookies.set('surveys_templates_jumbotron_hidden', true);
        },
        templateTypeLabel(slug) {
            let found = this.templateTypes.filter(type => type.value === slug)[0]
            if (found) return found.text;
        },
        async onCreateTemplate(data) {
            try {
                const {id} = await this.$store.dispatch('surveyTemplates/create', data);
                return this.$router.push({name: 'surveys-template-details', params: {template_id: id}})
            } catch ({response}) {
                if(response) {
                    this.notifyStatus(response.status, 'Validation Failed', response.data.message, response);
                }
            }
        },
        async onLoadTemplates() {
            await this.$store.dispatch('surveyTemplates/getList', {'get-pages': true, category: this.filterByCategory})
            this.loaded = true;
        },
        showPreview(templateId) {
            this.previewTemplateId = templateId;
            this.showPreviewModal = true;
        },
        async activateTemplate(templateId) {
            let survey = await this.$store.dispatch('surveys/create', {template_id: templateId});
            await this.$router.push({name: 'surveys-details-new', params: {survey_id: survey.id, }});
        }
    },
}
</script>
